// Defines the React components to associate with specific markdown tags
// i.e. what kind of h1 associates with # heading

import { Typography, Box, Link } from "@mui/material";
import { Components } from "react-markdown";
import { grey } from "../colors";

export const getMarkdownComponents = (
  textColor: string,
  fontSize?: string,
  lineHeight?: number | string,
): Partial<Components> => ({
  p: ({ children }) => (
    <Typography
      component="p"
      sx={{ my: "0.4em", color: textColor, fontSize: fontSize || "0.95rem", lineHeight: lineHeight || 1.7 }}
    >
      {children}
    </Typography>
  ),
  h1: ({ children }) => (
    <Typography
      variant="h4"
      sx={{ fontWeight: 700, fontSize: "1.5rem", color: textColor, mt: "1em", mb: "0.4em", lineHeight: 1.3 }}
    >
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography
      variant="h5"
      sx={{ fontWeight: 700, fontSize: "1.25rem", color: textColor, mt: "1em", mb: "0.35em", lineHeight: 1.3 }}
    >
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography
      variant="h6"
      sx={{ fontWeight: 700, fontSize: "1.1rem", color: textColor, mt: "0.8em", mb: "0.3em", lineHeight: 1.3 }}
    >
      {children}
    </Typography>
  ),
  h4: ({ children }) => (
    <Typography
      variant="subtitle1"
      sx={{ fontWeight: 700, fontSize: "1rem", color: textColor, mt: "0.6em", mb: "0.25em", lineHeight: 1.3 }}
    >
      {children}
    </Typography>
  ),
  ul: ({ node, ...props }: any) => (
    <Box
      component="ul"
      sx={{ pl: "1.5em", my: "0.3em", color: textColor, fontSize: fontSize || "0.95rem", listStyleType: "disc" }}
      {...props}
    />
  ),
  ol: ({ node, ...props }: any) => (
    <Box
      component="ol"
      sx={{ pl: "1.5em", my: "0.3em", color: textColor, fontSize: fontSize || "0.95rem" }}
      {...props}
    />
  ),
  li: ({ node, ...props }: any) => (
    <Box
      component="li"
      sx={{ mb: "0.35em", color: textColor, fontSize: fontSize || "0.95rem", lineHeight: lineHeight || 1.7 }}
      {...props}
    />
  ),
  code: ({ children, className }) => {
    const isBlock = !!className;
    if (isBlock) {
      return (
        <Box
          component="pre"
          sx={{
            backgroundColor: "#f5f5f5",
            borderRadius: "4px",
            p: "1em",
            overflow: "auto",
            my: "0.5em",
            "& code": { fontSize: fontSize || "0.875em", fontFamily: "monospace" },
          }}
        >
          <code className={className}>{children}</code>
        </Box>
      );
    }
    return (
      <Box
        component="code"
        sx={{
          backgroundColor: "#f5f5f5",
          borderRadius: "2px",
          px: "0.3em",
          py: "0.1em",
          fontSize: fontSize || "0.875em",
          fontFamily: "monospace",
        }}
      >
        {children}
      </Box>
    );
  },
  pre: ({ children }) => <>{children}</>,
  a: ({ href, children }) => (
    <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover">
      {children}
    </Link>
  ),
  strong: ({ children }) => (
    <Box component="strong" sx={{ fontWeight: 700, color: textColor }}>
      {children}
    </Box>
  ),
  em: ({ children }) => (
    <Box component="em" sx={{ fontStyle: "italic" }}>
      {children}
    </Box>
  ),
  blockquote: ({ children }) => (
    <Box sx={{ borderLeft: "3px solid #ccc", pl: "1em", my: "0.5em", color: "text.secondary" }}>{children}</Box>
  ),
  hr: () => <Box component="hr" sx={{ border: "none", borderTop: "1px solid #ddd", my: "1em" }} />,
  table: ({ node, ...props }: any) => (
    <Box sx={{ overflowX: "auto", my: "0.75em" }}>
      <Box
        component="table"
        sx={{
          borderCollapse: "collapse",
          width: "auto",
          minWidth: "50%",
          fontSize: fontSize || "0.95rem",
          color: textColor,
        }}
        {...props}
      />
    </Box>
  ),
  thead: ({ node, ...props }: any) => <Box component="thead" {...props} />,
  tbody: ({ node, ...props }: any) => <Box component="tbody" {...props} />,
  tr: ({ node, ...props }: any) => (
    <Box component="tr" sx={{ "&:nth-of-type(even)": { backgroundColor: grey[50] } }} {...props} />
  ),
  th: ({ node, style, ...props }: any) => (
    <Box
      component="th"
      sx={{
        border: `1px solid ${grey[200]}`,
        backgroundColor: grey[100],
        fontWeight: 700,
        p: "0.5em 0.75em",
        textAlign: style?.textAlign || "left",
        whiteSpace: "nowrap",
      }}
      {...props}
    />
  ),
  td: ({ node, style, ...props }: any) => (
    <Box
      component="td"
      sx={{
        border: `1px solid ${grey[200]}`,
        p: "0.5em 0.75em",
        textAlign: style?.textAlign || "left",
        verticalAlign: "top",
      }}
      {...props}
    />
  ),
});
